import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Panel } from 'primereact/panel';
import { Card } from 'primereact/card';
import { Chip } from 'primereact/chip';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatarParaMoedaBRL, formatarCNPJ, formatarCPF } from "../../../utils";
import NadaConsta from "../../../components/nadaConsta";
import { ScoreBar } from "../../../components/ScoreBar";

interface CreditoProps {
  credito: any;
  isConsultaNova?: boolean;
}

// ─── Reusable sub-section header ───────────────────────────────────────────────
function SectionHeader({ icon, label }: { icon: string; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '24px 0 12px',
        paddingBottom: '8px',
        borderBottom: '1px solid #E5E7EB',
      }}
    >
      <i className={icon} style={{ color: '#2563EB', fontSize: '14px' }} />
      <span style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 'small' }}>{label}</span>
    </div>
  );
}

// ─── Reusable styled table ──────────────────────────────────────────────────────
function ModernTable({ cols, rows }: { cols: { field: string | ((row: any) => any); label: string }[]; rows: any[] }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #F3F4F6' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'small' }}>
        <thead>
          <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
            {cols.map((c, i) => (
              <th
                key={i}
                style={{
                  padding: '10px 14px',
                  fontWeight: 700,
                  color: '#6B7280',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => (
            <tr
              key={idx}
              style={{ borderBottom: idx < rows.length - 1 ? '1px solid #F3F4F6' : 'none' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EFF6FF')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {cols.map((c, i) => (
                <td key={i} style={{ padding: '10px 14px', color: '#374151' }}>
                  {typeof c.field === 'function' ? c.field(row) : (row[c.field] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Score Gauge SVG Component ──────────────────────────────────────────────────
function ScoreGaugeSVG({ score, color }: { score: number; color: string }) {
  const radius = 70;
  const strokeWidth = 14;
  const circumference = Math.PI * radius; // semicircle
  const percentage = Math.max(0, Math.min(1000, score)) / 1000;
  const strokeDashoffset = circumference - percentage * circumference;

  // Pointer angle on the arc: -180deg (left) → 0deg (right)
  const angle = -180 + percentage * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 100;
  const cy = 96;
  const px = cx + radius * Math.cos(rad);
  const py = cy + radius * Math.sin(rad);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="200" height="110" viewBox="0 0 200 110" style={{ overflow: 'visible' }}>
        {/* Track */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease-in-out' }}
        />
        {/* Pointer dot */}
        <circle cx={px} cy={py} r={7} fill={color} stroke="#FFF" strokeWidth={2} />
        {/* Score text */}
        <text x="100" y="88" textAnchor="middle" fontSize="28" fontWeight="800" fill={color}>
          {score}
        </text>
        <text x="100" y="104" textAnchor="middle" fontSize="10" fill="#9CA3AF" fontWeight="600">
          de 1000
        </text>
      </svg>
    </div>
  );
}

// ─── Risk Bar Component ────────────────────────────────────────────────────────
function RiskBar({ scoreValue }: { scoreValue: number }) {
  const position = Math.min(100, Math.max(0, ((scoreValue - 300) / 700) * 100));

  const ranges = [
    { label: 'Muito Alto', range: '300 a 500', color: '#DE1616' },
    { label: 'Alto', range: '500 a 600', color: '#FF9700' },
    { label: 'Médio', range: '600 a 700', color: '#FFCA39' },
    { label: 'Baixo', range: '700 a 900', color: '#0DAB01' },
    { label: 'Muito Baixo', range: '900 a 1000', color: '#1FA685' },
  ];

  return (
    <div>
      <p style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#374151', margin: '0 0 8px 0' }}>
        Entenda a distribuição da faixa de risco
      </p>
      {/* Gradient bar */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '14px',
          borderRadius: '7px',
          background:
            'linear-gradient(to right, #DE1616 0%, #DE1616 28.5%, #FF9700 28.5%, #FF9700 42.8%, #FFCA39 42.8%, #FFCA39 57.1%, #0DAB01 57.1%, #0DAB01 85.6%, #1FA685 85.6%, #1FA685 100%)',
          marginBottom: '14px',
          marginTop: '8px',
        }}
      >
        {/* Marker */}
        <div
          style={{
            position: 'absolute',
            left: `${position}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '6px',
            height: '22px',
            borderRadius: '3px',
            backgroundColor: '#1F2937',
            border: '1px solid #111827',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'left 0.8s ease-in-out',
          }}
        />
      </div>

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
        {ranges.map(r => (
          <div key={r.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: r.color,
                display: 'inline-block',
                marginBottom: '2px',
              }}
            />
            <span style={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textAlign: 'center' }}>
              {r.label}
            </span>
            <span style={{ fontSize: '0.6rem', color: '#9CA3AF', textAlign: 'center' }}>{r.range}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const parseBRLFloat = (valStr: any): number => {
  if (valStr === undefined || valStr === null || valStr === '') return 0;
  if (typeof valStr === 'number') return valStr;
  const clean = valStr.toString().replace(/\./g, '').replace(',', '.');
  const num = parseFloat(clean);
  return isNaN(num) ? 0 : num;
};

const formatEndereco = (endStr: string) => {
  if (!endStr) return 'Não informado';
  return endStr.replace(/\|/g, ' | ').replace(/CEP_/g, 'CEP: ');
};

const formatTelefone = (telStr: string) => {
  if (!telStr) return '';
  const digits = telStr.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 8 || digits.length === 9) {
    return `${digits.slice(0, digits.length - 4)}-${digits.slice(digits.length - 4)}`;
  }
  return telStr;
};

const formatRowTelefone = (rowData: any) => {
  const ddd = String(rowData.DDD || '').trim();
  const num = String(rowData.NUM_TELEFONE || '').trim();
  if (ddd && !num.startsWith(ddd)) {
    return formatTelefone(ddd + num);
  }
  return formatTelefone(num);
};

const getScoreColor = (scoreValue: number) => {
  if (scoreValue <= 300) return '#DE1616'; // Vermelho (Risco Muito Alto)
  if (scoreValue <= 500) return '#FF9700'; // Laranja (Risco Alto)
  if (scoreValue <= 700) return '#FFCA39'; // Amarelo (Risco Médio)
  if (scoreValue <= 900) return '#0DAB01'; // Verde Claro (Risco Baixo)
  return '#1FA685'; // Verde Escuro (Risco Muito Baixo)
};

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parts = dateStr.split(/[\/: ]/);
  if (parts.length < 3) return null;
  const dia = parseInt(parts[0], 10);
  const mes = parseInt(parts[1], 10);
  const ano = parseInt(parts[2], 10);
  if (isNaN(dia) || isNaN(mes) || isNaN(ano)) return null;
  return new Date(ano, mes - 1, dia);
};

const getLatestDateStr = (occurrences: any[], dateField: string): string => {
  let latestDate: Date | null = null;
  let latestStr = '';
  occurrences.forEach((oc: any) => {
    const val = oc[dateField];
    if (val) {
      const parsed = parseDate(val);
      if (parsed) {
        if (!latestDate || parsed > latestDate) {
          latestDate = parsed;
          latestStr = val;
        }
      }
    }
  });
  return latestStr;
};

const getRiscoConfig = (riscoStr: string) => {
  const nivel = (riscoStr || '').toUpperCase();
  if (nivel.includes('ALTÍSSIMO') || nivel.includes('ALTISSIMO')) {
    return { color: '#DE1616', label: 'Altíssimo', bg: '#FDE8E8', text: '#9B1C1C', border: '#F8B4B4', range: '300 a 400' };
  }
  if (nivel.includes('MUITO ALTO')) {
    return { color: '#DE1616', label: 'Muito Alto', bg: '#FDE8E8', text: '#9B1C1C', border: '#F8B4B4', range: '400 a 500' };
  }
  if (nivel.includes('ALTO')) {
    return { color: '#FF9700', label: 'Alto', bg: '#FFF3E0', text: '#B45309', border: '#FCD34D', range: '500 a 600' };
  }
  if (nivel.includes('MEDIO') || nivel.includes('MÉDIO')) {
    return { color: '#FFCA39', label: 'Médio', bg: '#FEFCE8', text: '#854D0E', border: '#FEF08A', range: '600 a 700' };
  }
  if (nivel.includes('BAIXO')) {
    return { color: '#0DAB01', label: 'Baixo', bg: '#DEF7EC', text: '#03543F', border: '#84E1BC', range: '700 a 900' };
  }
  if (nivel.includes('MÍNIMO') || nivel.includes('MUITO BAIXO') || nivel.includes('MINIMO')) {
    return { color: '#1FA685', label: 'Muito Baixo', bg: '#ECFDF5', text: '#065F46', border: '#6EE7B7', range: '900 a 1000' };
  }
  return { color: '#9CA3AF', label: riscoStr || 'N/A', bg: '#F3F4F6', text: '#374151', border: '#D1D5DB', range: '' };
};

export default function Credito({ credito, isConsultaNova }: CreditoProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [useHighcharts, setUseHighcharts] = useState(false);

  if (!credito) return null;

  const dadosRF = credito.DADOS_RECEITA_FEDERAL || {};
  const infoEmpresa = credito.INFORMACOES_DA_EMPRESA || {};
  const isPJ = dadosRF.TIPO_PESSOA === 'JURIDICA';

  // Extract score details
  const scoreObj = credito.SCORES?.OCORRENCIAS?.[0] || {};
  const scoreValue = Number(scoreObj.SCORE || 0);
  const scoreTexto = scoreObj.TEXTO || '';
  const scoreProbInadimplencia = scoreObj.PROBABILIDADE_INADIMPLENCIA || '';
  const scoreRisco = scoreObj.RISCO || '';
  const labelColor = getScoreColor(scoreValue);
  const riscoConfig = getRiscoConfig(scoreRisco || scoreTexto);

  // Highcharts Half-Circle Donut Options
  const highchartsOptions = {
    chart: {},
    title: {
      text: scoreValue > 0 ? scoreValue.toString() : 'N/A',
      align: 'center',
      verticalAlign: 'middle',
      y: 90,
      style: {
        fontSize: '40px',
        color: scoreValue > 0 ? labelColor : '#6c757d'
      }
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: {
          enabled: false,
          distance: -50,
          style: {
            fontWeight: 'bold',
            color: 'white',
          }
        },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '75%']
      }
    },
    series: [{
      type: 'pie',
      name: '',
      innerSize: '60%',
      data: scoreValue > 0 ? [
        { name: '', y: scoreValue, color: labelColor, selected: true, showInLegend: false },
        { name: '', y: 1000 - scoreValue, color: '#ccc', selected: true, showInLegend: false }
      ] : [
        { name: '', y: 0, color: '#ccc', selected: true, showInLegend: false },
        { name: '', y: 1000, color: '#ccc', selected: true, showInLegend: false }
      ]
    }]
  };

  // Calculate Restrições
  const pefinCount = Number(credito.PEND_FINANCEIRAS?.QUANTIDADE_OCORRENCIA || 0);
  const pefinValor = parseBRLFloat(credito.PEND_FINANCEIRAS?.VALOR_TOTAL);

  const refinCount = Number(credito.PEND_REFIN?.QUANTIDADE_OCORRENCIA || 0);
  const refinValor = parseBRLFloat(credito.PEND_REFIN?.VALOR_TOTAL);

  const vencCount = Number(credito.PEND_VENCIDAS?.QUANTIDADE_OCORRENCIA || 0);
  const vencValor = parseBRLFloat(credito.PEND_VENCIDAS?.VALOR_TOTAL);

  const restFinanceirasCount = Number(credito.RESTRICOES_FINANCEIRAS?.QUANTIDADE_OCORRENCIA || 0);
  const restFinanceirasValor = parseBRLFloat(credito.RESTRICOES_FINANCEIRAS?.VALOR_TOTAL);

  const bacenCount = Number(credito.CH_SEM_FUNDOS_BACEN?.QUANTIDADE_OCORRENCIA || 0);

  const protestoCount = Number(credito.PROTESTOS?.QUANTIDADE_OCORRENCIA || 0);
  const protestoValor = parseBRLFloat(credito.PROTESTOS?.VALOR_TOTAL);

  const protestoSinteticoCount = Number(credito.PROTESTO_SINTETICO?.QUANTIDADE_OCORRENCIA || 0);
  const protestoSinteticoValor = parseBRLFloat(credito.PROTESTO_SINTETICO?.VALOR_TOTAL);

  const totalRestricoesCount = pefinCount + refinCount + vencCount + restFinanceirasCount + bacenCount + protestoCount + protestoSinteticoCount;
  const totalRestricoesValor = pefinValor + refinValor + vencValor + restFinanceirasValor + protestoValor + protestoSinteticoValor;

  const getBaseData = (filterFn: (inf: string) => boolean) => {
    const pefinOccurrences = (credito.PEND_FINANCEIRAS?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const refinOccurrences = (credito.PEND_REFIN?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const vencOccurrences = (credito.PEND_VENCIDAS?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const restFinOccurrences = (credito.RESTRICOES_FINANCEIRAS?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const bacenOccurrences = (credito.CH_SEM_FUNDOS_BACEN?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const protestosOccurrences = (credito.PROTESTOS?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );
    const protestoSinteticoOccurrences = (credito.PROTESTO_SINTETICO?.OCORRENCIAS || []).filter(
      (o: any) => filterFn(o.INFORMANTE || '')
    );

    const pefinCount = pefinOccurrences.length;
    const pefinValor = pefinOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const refinCount = refinOccurrences.length;
    const refinValor = refinOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const vencCount = vencOccurrences.length;
    const vencValor = vencOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const restFinanceirasCount = restFinOccurrences.length;
    const restFinanceirasValor = restFinOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const bacenCount = bacenOccurrences.length;

    const protestoCount = protestosOccurrences.length;
    const protestoValor = protestosOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const protestoSinteticoCount = protestoSinteticoOccurrences.length;
    const protestoSinteticoValor = protestoSinteticoOccurrences.reduce((acc: number, o: any) => acc + parseBRLFloat(o.VALOR), 0);

    const totalRestricoesCount = pefinCount + refinCount + vencCount + restFinanceirasCount + bacenCount + protestoCount + protestoSinteticoCount;
    const totalRestricoesValor = pefinValor + refinValor + vencValor + restFinanceirasValor + protestoValor + protestoSinteticoValor;

    const getLatestOccurrenceDate = () => {
      const dates: Date[] = [];
      pefinOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_INCLUSAO);
        if (d) dates.push(d);
      });
      refinOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_VENCIMENTO);
        if (d) dates.push(d);
      });
      vencOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_VENCIMENTO);
        if (d) dates.push(d);
      });
      restFinOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_INCLUSAO);
        if (d) dates.push(d);
      });
      bacenOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_EMISSAO);
        if (d) dates.push(d);
      });
      protestosOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_PROTESTO);
        if (d) dates.push(d);
      });
      protestoSinteticoOccurrences.forEach((o: any) => {
        const d = parseDate(o.DATA_OCORRENCIA);
        if (d) dates.push(d);
      });

      if (dates.length === 0) return 'Não informada';
      const latest = new Date(Math.max(...dates.map(d => d.getTime())));
      return latest.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return {
      pefinOccurrences,
      refinOccurrences,
      vencOccurrences,
      restFinOccurrences,
      bacenOccurrences,
      protestosOccurrences,
      protestoSinteticoOccurrences,
      pefinCount,
      refinCount,
      vencCount,
      restFinanceirasCount,
      bacenCount,
      protestoCount,
      protestoSinteticoCount,
      pefinValor,
      refinValor,
      vencValor,
      restFinanceirasValor,
      protestoValor,
      protestoSinteticoValor,
      totalRestricoesCount,
      totalRestricoesValor,
      latestOccurrenceDate: getLatestOccurrenceDate(),
    };
  };

  const isBaseI = (inf: string) => {
    const val = inf.toUpperCase().trim();
    return val === 'BASE I' || val.startsWith('BASE I ') || val.startsWith('BASE I-') || val.includes('PENDÊNCIAS') || val.includes('PENDENCIAS');
  };

  const isBaseII = (inf: string) => {
    const val = inf.toUpperCase().trim();
    return val === 'BASE II' || val.startsWith('BASE II ') || val.startsWith('BASE II-') || val.includes('DÍVIDAS') || val.includes('DIVIDAS');
  };

  const baseIData = getBaseData(isBaseI);
  const baseIIData = getBaseData(isBaseII);

  const getLatestOccurrenceDate = () => {
    const dates: Date[] = [];

    const pefin = credito.PEND_FINANCEIRAS?.OCORRENCIAS || [];
    pefin.forEach((o: any) => {
      const d = parseDate(o.DATA_INCLUSAO);
      if (d) dates.push(d);
    });

    const refin = credito.PEND_REFIN?.OCORRENCIAS || [];
    refin.forEach((o: any) => {
      const d = parseDate(o.DATA_VENCIMENTO);
      if (d) dates.push(d);
    });

    const venc = credito.PEND_VENCIDAS?.OCORRENCIAS || [];
    venc.forEach((o: any) => {
      const d = parseDate(o.DATA_VENCIMENTO);
      if (d) dates.push(d);
    });

    const restFin = credito.RESTRICOES_FINANCEIRAS?.OCORRENCIAS || [];
    restFin.forEach((o: any) => {
      const d = parseDate(o.DATA_INCLUSAO);
      if (d) dates.push(d);
    });

    const bacen = credito.CH_SEM_FUNDOS_BACEN?.OCORRENCIAS || [];
    bacen.forEach((o: any) => {
      const d = parseDate(o.DATA_EMISSAO);
      if (d) dates.push(d);
    });

    const prot = credito.PROTESTOS?.OCORRENCIAS || [];
    prot.forEach((o: any) => {
      const d = parseDate(o.DATA_PROTESTO);
      if (d) dates.push(d);
    });

    const protSint = credito.PROTESTO_SINTETICO?.OCORRENCIAS || [];
    protSint.forEach((o: any) => {
      const d = parseDate(o.DATA_OCORRENCIA);
      if (d) dates.push(d);
    });

    if (dates.length === 0) return 'Não informada';
    const latest = new Date(Math.max(...dates.map(d => d.getTime())));
    return latest.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getResumoPefin = (occurrences: any[], count: number, valor: number) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Registros',
        value: count
      });
      const latestDate = getLatestDateStr(occurrences, 'DATA_INCLUSAO');
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Última ocorrência',
        value: latestDate || 'Não informada'
      });
      resumos.push({
        icon: 'pi pi-dollar',
        label: 'Valor Total',
        value: formatarParaMoedaBRL(valor)
      });
    }
    return resumos;
  };

  const getResumoRefin = (occurrences: any[], count: number, valor: number, ultimoVencimentoGlobal?: string) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Registros',
        value: count
      });
      let latestDate = getLatestDateStr(occurrences, 'DATA_VENCIMENTO');
      if (!latestDate) {
        latestDate = ultimoVencimentoGlobal || '';
      }
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Último Vencimento',
        value: latestDate || 'Não informado'
      });
      resumos.push({
        icon: 'pi pi-dollar',
        label: 'Valor Total',
        value: formatarParaMoedaBRL(valor)
      });
    }
    return resumos;
  };

  const getResumoVencidas = (occurrences: any[], count: number, valor: number, ultimoVencimentoGlobal?: string) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Registros',
        value: count
      });
      let latestDate = getLatestDateStr(occurrences, 'DATA_VENCIMENTO');
      if (!latestDate) {
        latestDate = ultimoVencimentoGlobal || '';
      }
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Último Vencimento',
        value: latestDate || 'Não informado'
      });
      resumos.push({
        icon: 'pi pi-dollar',
        label: 'Valor Total',
        value: formatarParaMoedaBRL(valor)
      });
    }
    return resumos;
  };

  const getResumoRestFinanceiras = (occurrences: any[], count: number, valor: number) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Registros',
        value: count
      });
      const latestDate = getLatestDateStr(occurrences, 'DATA_INCLUSAO');
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Última ocorrência',
        value: latestDate || 'Não informada'
      });
      resumos.push({
        icon: 'pi pi-dollar',
        label: 'Valor Total',
        value: formatarParaMoedaBRL(valor)
      });
    }
    return resumos;
  };

  const getResumoBacen = (occurrences: any[], count: number, correntista?: string) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Ocorrências',
        value: count
      });
      const latestDate = getLatestDateStr(occurrences, 'DATA_EMISSAO');
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Última ocorrência',
        value: latestDate || 'Não informada'
      });
      resumos.push({
        icon: 'pi pi-user',
        label: 'Correntista',
        value: correntista || 'Não informado'
      });
    }
    return resumos;
  };

  const getResumoProtestos = (occurrences: any[], occurrencesSintetico: any[], count: number, valor: number, dataUltimoGlobal?: string, ultimoProtestoSintGlobal?: string) => {
    const resumos: any[] = [];
    if (count > 0) {
      resumos.push({
        icon: 'pi pi-chart-line',
        label: 'Quantidade Registros',
        value: count
      });
      let latestDateStr = getLatestDateStr(occurrences, 'DATA_PROTESTO');
      const latestSinteticoStr = getLatestDateStr(occurrencesSintetico, 'DATA_OCORRENCIA');
      const date1 = parseDate(latestDateStr);
      const date2 = parseDate(latestSinteticoStr);
      if (date2 && (!date1 || date2 > date1)) {
        latestDateStr = latestSinteticoStr;
      }
      if (!latestDateStr) {
        latestDateStr = dataUltimoGlobal || ultimoProtestoSintGlobal || '';
      }
      resumos.push({
        icon: 'pi pi-calendar-clock',
        label: 'Último Protesto',
        value: latestDateStr || 'Não informado'
      });
      resumos.push({
        icon: 'pi pi-dollar',
        label: 'Valor Total',
        value: formatarParaMoedaBRL(valor)
      });
    }
    return resumos;
  };

  const headerTemplatePefin = (options: any) => {
    const className = `${options.className} justify-content-between`;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('Pendências')}
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const headerTemplateRefin = (options: any) => {
    const className = `${options.className} justify-content-between`;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('pendRefin')}
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const headerTemplateVencidas = (options: any) => {
    const className = `${options.className} justify-content-between`;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('pendVencidas')}
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const headerTemplateRestFinanceiras = (options: any) => {
    const className = `${options.className} justify-content-between`;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('restricaoFinanceira')}
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const headerTemplateBacen = (options: any) => {
    const className = `${options.className} justify-content-between`;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('chequesSemFundos')} ({bacenCount})
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const headerTemplateProtestos = (options: any) => {
    const className = `${options.className} justify-content-between`;
    const totalCount = protestoCount || protestoSinteticoCount;
    return (
      <div className={className}>
        <div className="flex align-items-center">
          <span style={{ color: '#C70039' }} className="pi-consulta pi pi-exclamation-triangle"></span>
          <span className={options.titleClassName || "p-panel-title"} style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>
            {t('protestos')} ({totalCount})
          </span>
        </div>
        {options.iconsElement}
      </div>
    );
  };

  const passagens = credito.PASSAGENS_COMERCIAIS?.OCORRENCIAS || [];
  const quantidadePassagens = Number(credito.PASSAGENS_COMERCIAIS?.QUANTIDADE_OCORRENCIA || 0);

  const enderecos = credito.SOMENTE_ENDERECO?.DADOS || [];
  const telefones = credito.SOMENTE_TELEFONE?.DADOS || [];
  const emails = credito.EMAILS?.INFOEMAILS || [];

  const footerContent = (
    <div>
      <Button label="Fechar" icon="pi pi-times" style={{ color: '#183462' }} onClick={() => setVisible(false)} className="p-button-text" />
    </div>
  );

  const EntendaScoreContent = () => (
    <div className="modal-body">
      <h4 className="m-0 font-weight-bold text-success">Como ler o Quod Score?</h4>
      <br></br>
      <p>O Quod score é uma pontuação baseada principalmente nos dados do <b>cadastro positivo</b>, calculada a partir de técnicas estatísticas avançadas, que vai de 300 a 1000. Com ele você tem informações mais precisas sobre a probabilidade de pagamento do consumidor para tomar suas decisões com segurança.</p>
      <p>Quanto mais alto o score,<b> maior a chance de o consumidor pagar</b> seus compromissos financeiros nos próximos meses.</p>
      <p>Para facilitar sua análise, nós dividimos a pontuação de acordo com a probabilidade média de um grupo de consumidores honrar seus pagamentos em risco alto, médio e baixo.</p>
      <h4 className="m-0 font-weight-bold text-primary">Entenda o Cadastro Positivo</h4>
      <br></br>
      <p>O banco de dados do cadastro positivo <b>vai além de atrasos em pagamentos.</b> Analisamos mais de 100 atributos compartilhados por diversas fontes, considerando também os hábitos de pagamento do cliente e seu histórico de crédito.</p>
      <br></br>
      <h4 className="m-0 font-weight-bold text-info">Tipos de informações analisadas:</h4>
      <br></br>
      <ul className="list-group">
        <li className="list-group-item">Financiamentos</li>
        <li className="list-group-item">Empréstimos</li>
        <li className="list-group-item">Consórcio</li>
        <li className="list-group-item">Cheque Especial</li>
        <li className="list-group-item">Cartão de Crédito</li>
        <li className="list-group-item">Período de Contrato</li>
        <li className="list-group-item">Quantidade de contratos</li>
        <li className="list-group-item">Tipo de Produtos Contratados</li>
        <li className="list-group-item">Período Negativado</li>
        <li className="list-group-item">Quantidade de Negativações</li>
        <li className="list-group-item">Atrasos Vigentes</li>
        <li className="list-group-item">histórico de Atrasos</li>
      </ul>
      <br></br>
      <h4 className="m-0 font-weight-bold text-secondary">Origem dos dados positivos:</h4>
      <br></br>
      <ul className="list-group">
        <li className="list-group-item">Bancos</li>
        <li className="list-group-item">Instituições Financeiras</li>
        <li className="list-group-item">Empresas de Telefonia</li>
        <li className="list-group-item">Empresas de Energia Elétrica</li>
        <li className="list-group-item">Empresas de Água</li>
        <li className="list-group-item">Empresas de Gás</li>
        <li className="list-group-item">Empresas de Varejo</li>
        <li className="list-group-item">Empresas de Atacado</li>
        <li className="list-group-item">Fintechs</li>
        <li className="list-group-item">Empresas de Cartões</li>
        <li className="list-group-item">Empresas de Empréstimos</li>
      </ul>
    </div>
  );

  // ─── 1. NEW LAYOUT RENDER PATH ────────────────────────────────────────────────
  if (isConsultaNova) {
    return (
      <div className="flex flex-column gap-4" style={{ width: '100%' }}>
        {/* 1.1 CABEÇALHO CADASTRAL */}
        {isPJ && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-building" style={{ color: '#2563EB', fontSize: '18px' }} />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                {t('informacoesEmpresa')}
              </h3>
            </div>

            {/* Fields */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
                marginBottom: '8px',
              }}
            >
              {[
                { label: t('razaoSocial'), value: dadosRF.RAZAO_SOCIAL || infoEmpresa.RAZAO_SOCIAL || 'Não informado' },
                { label: t('nomeFantasia'), value: infoEmpresa.NOME_FANTASIA || dadosRF.NOME || 'Não informado' },
                { label: t('cnpj'), value: formatarCNPJ(infoEmpresa.CNPJ || '') || 'Não informado' },
                { label: t('status'), value: dadosRF.SITUACAO_RECEITA || infoEmpresa.SITUACAO || 'ATIVA' },
                { label: t('capitalSocial'), value: infoEmpresa.CAPITAL_SOCIAL ? formatarParaMoedaBRL(Number(infoEmpresa.CAPITAL_SOCIAL)) : 'Não informado' },
                { label: t('dataFundacaoNascimento'), value: dadosRF.DATA_NASCIMENTO_FUNDACAO || infoEmpresa.DATA_FUNDACAO || 'Não informado' },
                { label: t('naturezaJuridica'), value: dadosRF.NATUREZA_JURIDICA || infoEmpresa.NATUREZA_JURIDICA || 'Não informado' },
                { label: t('email'), value: dadosRF.EMAIL || infoEmpresa.EMAIL || 'Não informado' },
                { label: t('fone'), value: formatTelefone(infoEmpresa.TELEFONE || '') || 'Não informado' },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                    {f.label}
                  </span>
                  <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500, wordBreak: 'break-word' }}>
                    {f.value}
                  </span>
                </div>
              ))}
            </div>

            {infoEmpresa.ENDERECO_MATRIZ && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                  {t('enderecoMatriz')}
                </span>
                <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500 }}>
                  {`${infoEmpresa.ENDERECO_MATRIZ.ENDERECO}, ${infoEmpresa.ENDERECO_MATRIZ.BAIRRO} - ${infoEmpresa.ENDERECO_MATRIZ.CIDADE}/${infoEmpresa.ENDERECO_MATRIZ.UF} - CEP: ${infoEmpresa.ENDERECO_MATRIZ.CEP}`}
                </span>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                {t('atividadeEconomicaPrincipal')}
              </span>
              <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500 }}>
                {dadosRF.ATIVIDADE_ECONOMICA_PRINCIPAL || infoEmpresa.RAMO_ATIVIDADE_PRIMARIO || 'Não informado'}
              </span>
            </div>

            {infoEmpresa.RAMO_ATIVIDADE_SECUNDARIO && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {t('atividadeEconomicaSecundaria')}
                </span>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    border: '1px solid #E5E7EB',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#F9FAFB',
                  }}
                >
                  {infoEmpresa.RAMO_ATIVIDADE_SECUNDARIO.split(',').map((act: string, idx: number) => (
                    <div key={idx} style={{ fontSize: 'small', color: '#4B5563', padding: '4px 0' }}>
                      {act.trim()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 1.2 SCORE DE CRÉDITO */}
        {scoreValue > 0 && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            {/* Score Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    backgroundColor: '#EFF6FF',
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="pi pi-percentage" style={{ color: '#2563EB', fontSize: '18px' }} />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                  {t('scoreCredito')}
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setUseHighcharts(v => !v)}
                  title={useHighcharts ? 'Trocar para SVG nativo' : 'Trocar para Highcharts'}
                  style={{
                    background: 'none',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    color: '#6B7280',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <i className={useHighcharts ? 'pi pi-chart-pie' : 'pi pi-chart-bar'} style={{ fontSize: '12px' }} />
                  {useHighcharts ? 'Highcharts' : 'SVG Nativo'}
                </button>
                <span
                  style={{
                    backgroundColor: riscoConfig.bg,
                    color: riscoConfig.text,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    border: `1px solid ${riscoConfig.border}`,
                  }}
                >
                  Risco {riscoConfig.label}
                </span>
              </div>
            </div>

            {/* Gauge + Info side-by-side */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
                {useHighcharts ? (
                  <div style={{ width: '220px' }}>
                    <HighchartsReact highcharts={Highcharts} options={highchartsOptions} />
                  </div>
                ) : (
                  <ScoreGaugeSVG score={scoreValue} color={riscoConfig.color} />
                )}
              </div>

              <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Risco callout */}
                {scoreRisco && (
                  <div
                    style={{
                      backgroundColor: riscoConfig.bg,
                      border: `1px solid ${riscoConfig.border}`,
                      borderRadius: '12px',
                      padding: '14px 16px',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <i className="pi pi-exclamation-triangle" style={{ color: riscoConfig.color, marginTop: '2px' }}></i>
                      <div>
                        <span style={{ fontWeight: 700, color: riscoConfig.color, fontSize: '0.85rem' }}>
                          Nível de Risco: {scoreRisco}
                        </span>
                        {scoreProbInadimplencia && (
                          <p style={{ margin: '4px 0 0 0', fontSize: 'small', color: '#E11D48', fontWeight: 600 }}>
                            Probabilidade de Inadimplência: {scoreProbInadimplencia}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  label="Entenda o Score"
                  icon="pi pi-question-circle"
                  className="p-button-outlined p-button-sm"
                  style={{
                    borderRadius: '8px',
                    fontWeight: 600,
                    color: '#183462',
                    borderColor: '#183462',
                    alignSelf: 'flex-start',
                    padding: '6px 14px',
                  }}
                  onClick={() => setVisible(true)}
                />
              </div>
            </div>

            {/* Risk bar */}
            <RiskBar scoreValue={scoreValue} />

            {/* Motivos */}
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#4B5563', margin: 0 }}>
                  Motivos
                </h4>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
              </div>
              {scoreTexto ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    padding: '12px 16px',
                    backgroundColor: '#FFF7ED',
                    borderRadius: '8px',
                    border: '1px solid #FED7AA',
                  }}
                >
                  <i className="pi pi-info-circle" style={{ color: '#F59E0B', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: 'small', color: '#92400E', lineHeight: 1.5 }}>
                    {scoreTexto}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 'small', color: '#6B7280' }}>Não Informado</span>
              )}
            </div>
          </div>
        )}

        {/* 1.3.1 BASE I - Pendências Financeiras */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
            padding: '24px',
          }}
        >
          {/* Restrições Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div
              style={{
                backgroundColor: baseIData.totalRestricoesCount > 0 ? '#FEF2F2' : '#F0FDF4',
                borderRadius: '10px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i
                className={baseIData.totalRestricoesCount > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'}
                style={{ color: baseIData.totalRestricoesCount > 0 ? '#DC2626' : '#16A34A', fontSize: '18px' }}
              />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
              BASE I - Pendências Financeiras
            </h3>
            {baseIData.totalRestricoesCount > 0 && (
              <span
                style={{
                  marginLeft: 'auto',
                  backgroundColor: '#FEE2E2',
                  color: '#9B1C1C',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: '20px',
                }}
              >
                {baseIData.totalRestricoesCount} restrição/restrições
              </span>
            )}
          </div>

          {baseIData.totalRestricoesCount === 0 ? (
            <NadaConsta />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Summary cards */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '12px',
                }}
              >
                {[
                  { label: 'Quantidade Registros', value: baseIData.totalRestricoesCount, icon: 'pi pi-chart-line' },
                  { label: 'Última Ocorrência', value: baseIData.latestOccurrenceDate, icon: 'pi pi-calendar-clock' },
                  { label: 'Valor Total', value: formatarParaMoedaBRL(baseIData.totalRestricoesValor), icon: 'pi pi-dollar' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: '#1E3A8A',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ color: '#93C5FD', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                        {item.label}
                      </span>
                      <span style={{ color: '#FFFFFF', fontWeight: 800, fontSize: '1rem' }}>{item.value}</span>
                    </div>
                    <span
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className={item.icon} style={{ color: '#FFFFFF', fontSize: '14px' }} />
                    </span>
                  </div>
                ))}
              </div>

              {/* PEFIN */}
              {baseIData.pefinCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendenciasFinanceira')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_INCLUSAO', label: t('dtInclusao') },
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CREDOR', label: t('credor') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ backgroundColor: '#F3F4F6', color: '#374151', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.MODALIDADE}</span>, label: t('modalidade') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                    ]}
                    rows={baseIData.pefinOccurrences}
                  />
                </div>
              )}

              {/* REFIN */}
              {baseIData.refinCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendRefin')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: 'CREDOR', label: t('credor') },
                    ]}
                    rows={baseIData.refinOccurrences}
                  />
                </div>
              )}

              {/* Pendências Vencidas */}
              {baseIData.vencCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendVencidas')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: 'CREDOR', label: t('credor') },
                    ]}
                    rows={baseIData.vencOccurrences}
                  />
                </div>
              )}

              {/* Restrições Financeiras Específicas */}
              {baseIData.restFinanceirasCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('restricaoFinanceira')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_INCLUSAO', label: t('dtInclusao') },
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CREDOR', label: t('credor') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                    ]}
                    rows={baseIData.restFinOccurrences}
                  />
                </div>
              )}

              {/* Cheques Sem Fundos BACEN */}
              {baseIData.bacenCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={`${t('chequesSemFundos')} (${baseIData.bacenCount})`} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_EMISSAO', label: t('data') },
                      { field: 'BANCO', label: t('banco') },
                      { field: 'AGENCIA', label: t('agencia') },
                      { field: 'CONTA', label: 'Conta' },
                      { field: 'CHEQUE', label: 'Cheque' },
                      { field: 'VALOR', label: t('valor') },
                    ]}
                    rows={baseIData.bacenOccurrences}
                  />
                </div>
              )}

              {/* Protestos */}
              {(baseIData.protestoCount > 0 || baseIData.protestoSinteticoCount > 0) && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={`${t('protestos')} (${baseIData.protestoCount || baseIData.protestoSinteticoCount})`} />
                  {baseIData.protestosOccurrences.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <ModernTable
                        cols={[
                          { field: 'DATA_PROTESTO', label: 'Data Protesto' },
                          { field: 'CARTORIO', label: 'Cartório' },
                          { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: 'Valor' },
                        ]}
                        rows={baseIData.protestosOccurrences}
                      />
                    </div>
                  )}
                  {baseIData.protestoSinteticoOccurrences.length > 0 && (
                    <ModernTable
                      cols={[
                        { field: 'DATA_OCORRENCIA', label: t('data') },
                        { field: 'CARTORIO', label: t('cartorio') },
                        { field: 'COMARCA', label: t('cidade') },
                        { field: 'UF', label: t('uf') },
                        { field: 'VALOR', label: t('valor') },
                      ]}
                      rows={baseIData.protestoSinteticoOccurrences}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 1.3.2 BASE II - Dívidas vencidas */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
            padding: '24px',
          }}
        >
          {/* Restrições Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div
              style={{
                backgroundColor: baseIIData.totalRestricoesCount > 0 ? '#FEF2F2' : '#F0FDF4',
                borderRadius: '10px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i
                className={baseIIData.totalRestricoesCount > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'}
                style={{ color: baseIIData.totalRestricoesCount > 0 ? '#DC2626' : '#16A34A', fontSize: '18px' }}
              />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
              BASE II - Dívidas vencidas
            </h3>
            {baseIIData.totalRestricoesCount > 0 && (
              <span
                style={{
                  marginLeft: 'auto',
                  backgroundColor: '#FEE2E2',
                  color: '#9B1C1C',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: '20px',
                }}
              >
                {baseIIData.totalRestricoesCount} restrição/restrições
              </span>
            )}
          </div>

          {baseIIData.totalRestricoesCount === 0 ? (
            <NadaConsta />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Summary cards */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '12px',
                }}
              >
                {[
                  { label: 'Quantidade Registros', value: baseIIData.totalRestricoesCount, icon: 'pi pi-chart-line' },
                  { label: 'Última Ocorrência', value: baseIIData.latestOccurrenceDate, icon: 'pi pi-calendar-clock' },
                  { label: 'Valor Total', value: formatarParaMoedaBRL(baseIIData.totalRestricoesValor), icon: 'pi pi-dollar' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: '#1E3A8A',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ color: '#93C5FD', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                        {item.label}
                      </span>
                      <span style={{ color: '#FFFFFF', fontWeight: 800, fontSize: '1rem' }}>{item.value}</span>
                    </div>
                    <span
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className={item.icon} style={{ color: '#FFFFFF', fontSize: '14px' }} />
                    </span>
                  </div>
                ))}
              </div>

              {/* PEFIN */}
              {baseIIData.pefinCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendenciasFinanceira')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_INCLUSAO', label: t('dtInclusao') },
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CREDOR', label: t('credor') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ backgroundColor: '#F3F4F6', color: '#374151', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.MODALIDADE}</span>, label: t('modalidade') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                    ]}
                    rows={baseIIData.pefinOccurrences}
                  />
                </div>
              )}

              {/* REFIN */}
              {baseIIData.refinCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendRefin')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: 'CREDOR', label: t('credor') },
                    ]}
                    rows={baseIIData.refinOccurrences}
                  />
                </div>
              )}

              {/* Pendências Vencidas */}
              {baseIIData.vencCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('pendVencidas')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CONTRATO', label: t('contrato') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                      { field: row => <span style={{ backgroundColor: '#FEE2E2', color: '#9B1C1C', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>{row.TIPO_DEVEDOR}</span>, label: t('tipo') },
                      { field: 'CREDOR', label: t('credor') },
                    ]}
                    rows={baseIIData.vencOccurrences}
                  />
                </div>
              )}

              {/* Restrições Financeiras Específicas */}
              {baseIIData.restFinanceirasCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={t('restricaoFinanceira')} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_INCLUSAO', label: t('dtInclusao') },
                      { field: 'DATA_VENCIMENTO', label: t('dataVencimento') },
                      { field: 'CREDOR', label: t('credor') },
                      { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: t('valor') },
                    ]}
                    rows={baseIIData.restFinOccurrences}
                  />
                </div>
              )}

              {/* Cheques Sem Fundos BACEN */}
              {baseIIData.bacenCount > 0 && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={`${t('chequesSemFundos')} (${baseIIData.bacenCount})`} />
                  <ModernTable
                    cols={[
                      { field: 'DATA_EMISSAO', label: t('data') },
                      { field: 'BANCO', label: t('banco') },
                      { field: 'AGENCIA', label: t('agencia') },
                      { field: 'CONTA', label: 'Conta' },
                      { field: 'CHEQUE', label: 'Cheque' },
                      { field: 'VALOR', label: t('valor') },
                    ]}
                    rows={baseIIData.bacenOccurrences}
                  />
                </div>
              )}

              {/* Protestos */}
              {(baseIIData.protestoCount > 0 || baseIIData.protestoSinteticoCount > 0) && (
                <div>
                  <SectionHeader icon="pi pi-exclamation-triangle" label={`${t('protestos')} (${baseIIData.protestoCount || baseIIData.protestoSinteticoCount})`} />
                  {baseIIData.protestosOccurrences.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <ModernTable
                        cols={[
                          { field: 'DATA_PROTESTO', label: 'Data Protesto' },
                          { field: 'CARTORIO', label: 'Cartório' },
                          { field: row => <span style={{ color: '#DC2626', fontWeight: 700 }}>{formatarParaMoedaBRL(parseBRLFloat(row.VALOR))}</span>, label: 'Valor' },
                        ]}
                        rows={baseIIData.protestosOccurrences}
                      />
                    </div>
                  )}
                  {baseIIData.protestoSinteticoOccurrences.length > 0 && (
                    <ModernTable
                      cols={[
                        { field: 'DATA_OCORRENCIA', label: t('data') },
                        { field: 'CARTORIO', label: t('cartorio') },
                        { field: 'COMARCA', label: t('cidade') },
                        { field: 'UF', label: t('uf') },
                        { field: 'VALOR', label: t('valor') },
                      ]}
                      rows={baseIIData.protestoSinteticoOccurrences}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 1.4 PASSAGENS COMERCIAIS */}
        {quantidadePassagens > 0 && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-send" style={{ color: '#2563EB', fontSize: '18px' }} />
              </div>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                  {t('passagensComerciais')}
                </h3>
                <span style={{ fontSize: 'small', color: '#6B7280' }}>
                  {quantidadePassagens} passagem/passagens
                </span>
              </div>
            </div>

            <ModernTable
              cols={[
                { field: 'CLIENTE_CONSULTA', label: t('clienteConsulta') },
                { field: 'CIDADE_UF_CLIENTE', label: t('cidadeUfCliente') },
                { field: 'DATA_CONSULTA', label: t('dataConsulta') },
                { field: row => row.HORA_CONSULTA || 'Não informado', label: 'Hora Consulta' },
                { field: row => formatTelefone(row.TELEFONE_CLIENTE) || 'Não informado', label: 'Telefone' },
              ]}
              rows={passagens}
            />
          </div>
        )}

        {/* 1.5 LOCALIZAÇÃO E CONTATOS (Endereços, Telefones, Emails) */}
        {enderecos.length > 0 && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-map-marker" style={{ color: '#2563EB', fontSize: '18px' }} />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                {t('enderecos')}
              </h3>
            </div>
            <ModernTable
              cols={[
                { field: 'ENDERECO', label: t('ENDERECO') },
                { field: 'BAIRRO', label: t('bairro') },
                { field: 'NUMERO', label: t('numero') },
                { field: 'COMPLEMENTO', label: t('complemento') },
                { field: 'CIDADE', label: t('cidade') },
                { field: 'UF', label: t('UF') },
                { field: 'CEP', label: t('cep') },
              ]}
              rows={enderecos}
            />
          </div>
        )}

        {telefones.length > 0 && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-phone" style={{ color: '#2563EB', fontSize: '18px' }} />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                {t('fone')}
              </h3>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {telefones.map((tel: any, idx: number) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: '#EFF6FF',
                    color: '#1E40AF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '20px',
                    padding: '4px 14px',
                    fontSize: 'small',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-phone" style={{ fontSize: '11px', marginRight: '6px' }} />
                  {formatRowTelefone(tel)} <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>({tel.TIPO_TELEFONE})</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {emails.length > 0 && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div
                style={{
                  backgroundColor: '#F0FDF4',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-envelope" style={{ color: '#16A34A', fontSize: '18px' }} />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
                {t('email')}
              </h3>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {emails.map((e: any, idx: number) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: '#F0FDF4',
                    color: '#15803D',
                    border: '1px solid #BBF7D0',
                    borderRadius: '20px',
                    padding: '4px 14px',
                    fontSize: 'small',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-envelope" style={{ fontSize: '11px', marginRight: '6px' }} />
                  {e.ENDERECO}
                </span>
              ))}
            </div>
          </div>
        )}

        <Dialog header="Entenda o Score" visible={visible} style={{ width: '50vw' }} onHide={() => setVisible(false)} footer={footerContent}>
          <EntendaScoreContent />
        </Dialog>
      </div>
    );
  }

  // ─── 2. LEGACY LAYOUT RENDER PATH ─────────────────────────────────────────────
  const headerMercado = () => (
    <div className="flex-score align-items-center" style={{ padding: '1.5rem' }}>
      <div className="flex-score justify-content-center align-items-center p-2 border-round" style={{ backgroundColor: 'rgb(100, 181, 246)' }}>
        <i className="pi pi-shopping-cart" style={{ color: "rgb(25, 118, 210)" }}></i>
      </div>
      <span className="text-xl ml-2 font-semibold">Perfil de Mercado</span>
    </div>
  );

  const headerPontualidade = () => (
    <div className="flex-score align-items-center" style={{ padding: '1.5rem' }}>
      <div className="flex-score justify-content-center align-items-center p-2 border-round" style={{ backgroundColor: 'rgb(77, 182, 172)' }}>
        <i className="pi pi-dollar" style={{ color: "rgb(0, 121, 107)" }}></i>
      </div>
      <span className="text-xl ml-2 font-semibold">Pontualidade de Pagamento</span>
    </div>
  );

  const options = {
    chart: {},
    title: {
      text: scoreValue > 0 ? scoreValue.toString() : 'N/A',
      align: 'center',
      verticalAlign: 'middle',
      y: 90,
      style: {
        fontSize: '40px',
        color: scoreValue > 0 ? labelColor : '#6c757d'
      }
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: {
          enabled: false,
          distance: -50,
          style: {
            fontWeight: 'bold',
            color: 'white',
          }
        },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '75%']
      }
    },
    series: [{
      type: 'pie',
      name: '',
      innerSize: '60%',
      data: scoreValue > 0 ? [
        { name: '', y: scoreValue, color: labelColor, selected: true, showInLegend: false },
        { name: '', y: 1000 - scoreValue, color: '#ccc', selected: true, showInLegend: false }
      ] : [
        { name: '', y: 0, color: '#ccc', selected: true, showInLegend: false },
        { name: '', y: 1000, color: '#ccc', selected: true, showInLegend: false }
      ]
    }]
  };

  return (
    <div className="flex flex-column gap-4" style={{ width: '100%' }}>
      <div className="pagebreak"></div>

      {/* 2.1 CABEÇALHO CADASTRAL */}
      {isPJ && (
        <>
          <Panel header={t('informacoesEmpresa')}>
            <div className="grid">
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('razaoSocial')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.RAZAO_SOCIAL || infoEmpresa.RAZAO_SOCIAL || 'Não informado'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('nomeFantasia')}</label>
                <Chip style={{ width: "100%" }} label={infoEmpresa.NOME_FANTASIA || dadosRF.NOME || 'Não informado'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('cnpj')}</label>
                <Chip style={{ width: "100%" }} label={formatarCNPJ(infoEmpresa.CNPJ || '') || 'Não informado'} />
              </div>

              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('status')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.SITUACAO_RECEITA || infoEmpresa.SITUACAO || 'ATIVA'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('capitalSocial')}</label>
                <Chip style={{ width: "100%" }} label={infoEmpresa.CAPITAL_SOCIAL ? formatarParaMoedaBRL(Number(infoEmpresa.CAPITAL_SOCIAL)) : 'Não informado'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('dataFundacaoNascimento')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.DATA_NASCIMENTO_FUNDACAO || infoEmpresa.DATA_FUNDACAO || 'Não informado'} />
              </div>

              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('naturezaJuridica')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.NATUREZA_JURIDICA || infoEmpresa.NATUREZA_JURIDICA || 'Não informado'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('email')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.EMAIL || infoEmpresa.EMAIL || 'Não informado'} />
              </div>
              <div className="col-12 md:col-6 lg:col-4 p-fluid">
                <label className="label-dados">{t('fone')}</label>
                <Chip style={{ width: "100%" }} label={formatTelefone(infoEmpresa.TELEFONE || '') || 'Não informado'} />
              </div>

              {infoEmpresa.ENDERECO_MATRIZ && (
                <div className="col-12 p-fluid">
                  <label className="label-dados">{t('enderecoMatriz')}</label>
                  <Chip
                    style={{ width: "100%" }}
                    label={`${infoEmpresa.ENDERECO_MATRIZ.ENDERECO}, ${infoEmpresa.ENDERECO_MATRIZ.BAIRRO} - ${infoEmpresa.ENDERECO_MATRIZ.CIDADE}/${infoEmpresa.ENDERECO_MATRIZ.UF} - CEP: ${infoEmpresa.ENDERECO_MATRIZ.CEP}`}
                  />
                </div>
              )}

              <div className="col-12 p-fluid">
                <label className="label-dados">{t('atividadeEconomicaPrincipal')}</label>
                <Chip style={{ width: "100%" }} label={dadosRF.ATIVIDADE_ECONOMICA_PRINCIPAL || infoEmpresa.RAMO_ATIVIDADE_PRIMARIO || 'Não informado'} />
              </div>

              {infoEmpresa.RAMO_ATIVIDADE_SECUNDARIO && (
                <div className="col-12 p-fluid">
                  <label className="label-dados">{t('atividadeEconomicaSecundaria')}</label>
                  <div className="flex flex-column gap-2" style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #e9ecef', padding: '0.5rem', borderRadius: '6px' }}>
                    {infoEmpresa.RAMO_ATIVIDADE_SECUNDARIO.split(',').map((act: string, idx: number) => (
                      <div key={idx} className="p-2 surface-100 border-round text-sm">
                        {act.trim()}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Panel>
          <br></br>
        </>
      )}

      {/* 2.3 RESTRIÇÕES / APONTAMENTOS */}
      <Panel header={t('restricoesMercado')}>
        {totalRestricoesCount === 0 ? (
          <NadaConsta />
        ) : (
          <div className="flex flex-column gap-4">
            {/* BASE I - Pendências Financeiras */}
            <Panel header="Pendências Financeiras" toggleable>
              {baseIData.totalRestricoesCount === 0 ? (
                <NadaConsta />
              ) : (
                <div className="flex flex-column gap-3">
                  {baseIData.pefinCount > 0 && (
                    <Panel header={"BASE I - Pendências Financeiras"} toggleable>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoPefin(baseIData.pefinOccurrences, baseIData.pefinCount, baseIData.pefinValor).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      <br />
                      <DataTable size="small" value={baseIData.pefinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                        <Column field="DATA_INCLUSAO" header={t('dtInclusao')}></Column>
                        <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                        <Column field="CREDOR" header={t('credor')}></Column>
                        <Column field="CONTRATO" header={t('contrato')}></Column>
                        <Column field="MODALIDADE" header={t('modalidade')}></Column>
                        <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                        <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                      </DataTable>
                    </Panel>
                  )}

                  {baseIData.refinCount > 0 && (
                    <Panel headerTemplate={headerTemplateRefin} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoRefin(baseIData.refinOccurrences, baseIData.refinCount, baseIData.refinValor, credito.PEND_REFIN?.ULTIMO_VENCIMENTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIData.refinOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIData.refinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                            <Column field="CONTRATO" header={t('contrato')}></Column>
                            <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                            <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                            <Column field="CREDOR" header={t('credor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {baseIData.vencCount > 0 && (
                    <Panel headerTemplate={headerTemplateVencidas} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoVencidas(baseIData.vencOccurrences, baseIData.vencCount, baseIData.vencValor, credito.PEND_VENCIDAS?.ULTIMO_VENCIMENTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIData.vencOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIData.vencOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                            <Column field="CONTRATO" header={t('contrato')}></Column>
                            <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                            <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                            <Column field="CREDOR" header={t('credor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {baseIData.restFinanceirasCount > 0 && (
                    <Panel headerTemplate={headerTemplateRestFinanceiras} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoRestFinanceiras(baseIData.restFinOccurrences, baseIData.restFinanceirasCount, baseIData.restFinanceirasValor).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      <br />
                      <DataTable size="small" value={baseIData.restFinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                        <Column field="DATA_INCLUSAO" header={t('dtInclusao')}></Column>
                        <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                        <Column field="CREDOR" header={t('credor')}></Column>
                        <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                      </DataTable>
                    </Panel>
                  )}

                  {baseIData.bacenCount > 0 && (
                    <Panel headerTemplate={headerTemplateBacen} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoBacen(baseIData.bacenOccurrences, baseIData.bacenCount, credito.CH_SEM_FUNDOS_BACEN?.CORRENTISTA).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIData.bacenOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIData.bacenOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_EMISSAO" header={t('data')}></Column>
                            <Column field="BANCO" header={t('banco')}></Column>
                            <Column field="AGENCIA" header={t('agencia')}></Column>
                            <Column field="CONTA" header="Conta"></Column>
                            <Column field="CHEQUE" header="Cheque"></Column>
                            <Column field="VALOR" header={t('valor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {(baseIData.protestoCount > 0 || baseIData.protestoSinteticoCount > 0) && (
                    <Panel headerTemplate={headerTemplateProtestos} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoProtestos(baseIData.protestosOccurrences, baseIData.protestoSinteticoOccurrences, baseIData.protestoCount || baseIData.protestoSinteticoCount, baseIData.protestoValor + baseIData.protestoSinteticoValor, credito.PROTESTOS?.DATA_ULTIMO, credito.PROTESTO_SINTETICO?.ULTIMO_PROTESTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIData.protestosOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIData.protestosOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_PROTESTO" header="Data Protesto"></Column>
                            <Column field="CARTORIO" header="Cartório"></Column>
                            <Column field="VALOR" header="Valor" body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                          </DataTable>
                        </>
                      )}
                      {baseIData.protestoSinteticoOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIData.protestoSinteticoOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_OCORRENCIA" header={t('data')}></Column>
                            <Column field="CARTORIO" header={t('cartorio')}></Column>
                            <Column field="COMARCA" header={t('cidade')}></Column>
                            <Column field="UF" header={t('uf')}></Column>
                            <Column field="VALOR" header={t('valor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}
                </div>
              )}
            </Panel>
            <br></br>
            {/* BASE II - Dívidas vencidas */}
            <Panel header="Dívidas vencidas" toggleable>
              {baseIIData.totalRestricoesCount === 0 ? (
                <NadaConsta />
              ) : (
                <div className="flex flex-column gap-3">
                  {baseIIData.pefinCount > 0 && (
                    <Panel header="BASE II - Dívidas vencidas" toggleable>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoPefin(baseIIData.pefinOccurrences, baseIIData.pefinCount, baseIIData.pefinValor).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      <br />
                      <DataTable size="small" value={baseIIData.pefinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                        <Column field="DATA_INCLUSAO" header={t('dtInclusao')}></Column>
                        <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                        <Column field="CREDOR" header={t('credor')}></Column>
                        <Column field="CONTRATO" header={t('contrato')}></Column>
                        <Column field="MODALIDADE" header={t('modalidade')}></Column>
                        <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                        <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                      </DataTable>
                    </Panel>
                  )}

                  {baseIIData.refinCount > 0 && (
                    <Panel headerTemplate={headerTemplateRefin} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoRefin(baseIIData.refinOccurrences, baseIIData.refinCount, baseIIData.refinValor, credito.PEND_REFIN?.ULTIMO_VENCIMENTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIIData.refinOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIIData.refinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                            <Column field="CONTRATO" header={t('contrato')}></Column>
                            <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                            <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                            <Column field="CREDOR" header={t('credor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {baseIIData.vencCount > 0 && (
                    <Panel headerTemplate={headerTemplateVencidas} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoVencidas(baseIIData.vencOccurrences, baseIIData.vencCount, baseIIData.vencValor, credito.PEND_VENCIDAS?.ULTIMO_VENCIMENTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIIData.vencOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIIData.vencOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                            <Column field="CONTRATO" header={t('contrato')}></Column>
                            <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                            <Column field="TIPO_DEVEDOR" header={t('tipo')}></Column>
                            <Column field="CREDOR" header={t('credor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {baseIIData.restFinanceirasCount > 0 && (
                    <Panel headerTemplate={headerTemplateRestFinanceiras} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoRestFinanceiras(baseIIData.restFinOccurrences, baseIIData.restFinanceirasCount, baseIIData.restFinanceirasValor).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      <br />
                      <DataTable size="small" value={baseIIData.restFinOccurrences} tableStyle={{ minWidth: '50rem' }}>
                        <Column field="DATA_INCLUSAO" header={t('dtInclusao')}></Column>
                        <Column field="DATA_VENCIMENTO" header={t('dataVencimento')}></Column>
                        <Column field="CREDOR" header={t('credor')}></Column>
                        <Column field="VALOR" header={t('valor')} body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                      </DataTable>
                    </Panel>
                  )}

                  {baseIIData.bacenCount > 0 && (
                    <Panel headerTemplate={headerTemplateBacen} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoBacen(baseIIData.bacenOccurrences, baseIIData.bacenCount, credito.CH_SEM_FUNDOS_BACEN?.CORRENTISTA).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIIData.bacenOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIIData.bacenOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_EMISSAO" header={t('data')}></Column>
                            <Column field="BANCO" header={t('banco')}></Column>
                            <Column field="AGENCIA" header={t('agencia')}></Column>
                            <Column field="CONTA" header="Conta"></Column>
                            <Column field="CHEQUE" header="Cheque"></Column>
                            <Column field="VALOR" header={t('valor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}

                  {(baseIIData.protestoCount > 0 || baseIIData.protestoSinteticoCount > 0) && (
                    <Panel headerTemplate={headerTemplateProtestos} toggleable collapsed>
                      <div className="flex-consulta flex-wrap gap-3">
                        {getResumoProtestos(baseIIData.protestosOccurrences, baseIIData.protestoSinteticoOccurrences, baseIIData.protestoCount || baseIIData.protestoSinteticoCount, baseIIData.protestoValor + baseIIData.protestoSinteticoValor, credito.PROTESTOS?.DATA_ULTIMO, credito.PROTESTO_SINTETICO?.ULTIMO_PROTESTO).map((item: any, index: number) => (
                          <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
                            <div className="flex-consulta justify-content-between gap-5">
                              <div className="flex-consulta flex-column gap-1">
                                <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                                <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                              </div>
                              <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                                <i className={item.icon} />
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {baseIIData.protestosOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIIData.protestosOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_PROTESTO" header="Data Protesto"></Column>
                            <Column field="CARTORIO" header="Cartório"></Column>
                            <Column field="VALOR" header="Valor" body={(rowData) => formatarParaMoedaBRL(parseBRLFloat(rowData.VALOR))}></Column>
                          </DataTable>
                        </>
                      )}
                      {baseIIData.protestoSinteticoOccurrences.length > 0 && (
                        <>
                          <br />
                          <DataTable size="small" value={baseIIData.protestoSinteticoOccurrences} tableStyle={{ minWidth: '50rem' }}>
                            <Column field="DATA_OCORRENCIA" header={t('data')}></Column>
                            <Column field="CARTORIO" header={t('cartorio')}></Column>
                            <Column field="COMARCA" header={t('cidade')}></Column>
                            <Column field="UF" header={t('uf')}></Column>
                            <Column field="VALOR" header={t('valor')}></Column>
                          </DataTable>
                        </>
                      )}
                    </Panel>
                  )}
                </div>
              )}
            </Panel>
          </div>
        )}
      </Panel>
      <br></br>

      {/* 2.4 PASSAGENS COMERCIAIS */}
      {quantidadePassagens > 0 && (
        <Panel header={`${t('passagensComerciais')}`}>
          <DataTable size="small" value={passagens} tableStyle={{ minWidth: '50rem' }}>
            <Column field="CLIENTE_CONSULTA" header={t('clienteConsulta')}></Column>
            <Column field="CIDADE_UF_CLIENTE" header={t('cidadeUfCliente')}></Column>
            <Column field="DATA_CONSULTA" header={t('dataConsulta')}></Column>
            <Column field="HORA_CONSULTA" header="Hora Consulta" body={(rowData) => rowData.HORA_CONSULTA || 'Não informado'}></Column>
            <Column field="TELEFONE_CLIENTE" header="Telefone" body={(rowData) => formatTelefone(rowData.TELEFONE_CLIENTE) || 'Não informado'}></Column>
          </DataTable>
        </Panel>
      )}

      {/* 2.5 LOCALIZAÇÃO E CONTATOS (Endereços, Telefones, Emails) */}
      {enderecos.length > 0 && (
        <div>
          <Panel header={t('enderecos')}>
            <DataTable size="small" value={enderecos} tableStyle={{ minWidth: '50rem' }}>
              <Column field="ENDERECO" header={t('ENDERECO')}></Column>
              <Column field="BAIRRO" header={t('bairro')}></Column>
              <Column field="NUMERO" header={t('numero')}></Column>
              <Column field="COMPLEMENTO" header={t('complemento')}></Column>
              <Column field="CIDADE" header={t('cidade')}></Column>
              <Column field="UF" header={t('UF')}></Column>
              <Column field="CEP" header={t('cep')}></Column>
            </DataTable>
          </Panel>
          <br></br>
        </div>
      )}
      <br />
      {telefones.length > 0 && (
        <div>
          <Panel header={t('fone')}>
            <DataTable size="small" value={telefones} tableStyle={{ minWidth: '50rem' }}>
              <Column field="NUM_TELEFONE" header={t('fone')} body={formatRowTelefone}></Column>
              <Column field="TIPO_TELEFONE" header={t('Tipo de Telefone')}></Column>
            </DataTable>
          </Panel>
          <br></br>
        </div>
      )}
      <br />
      {emails.length > 0 && (
        <div>
          <Panel header={t('email')}>
            <DataTable size="small" value={emails} tableStyle={{ minWidth: '50rem' }}>
              <Column field="ENDERECO" header={t('email')}></Column>
            </DataTable>
          </Panel>
          <br></br>
        </div>
      )}
      <br />
    </div>
  );
}
